// components/entities/scoreMotion.tsx
//
// Motion primitives for the shortlist tables. Editing a rubric or a doctrine has to FEEL
// like direct manipulation: the score the user just changed counts up (or down) to its new
// value, flashes its direction, and the row settles. Nothing here re-renders React while
// an animation runs, every frame is written straight to the DOM node, because a re-score
// rewrites hundreds of rows at once and a per-frame React commit per row would drop frames.
//
// Two rules make this cheap:
//  1. ONE requestAnimationFrame loop for the whole page (the shared ticker below), so a burst
//     of socket updates coalesces into a single frame instead of one loop per row.
//  2. Only paint/composite properties move (textContent, color, transform, box-shadow). Nothing
//     here touches a layout property, and nothing forces a synchronous reflow.
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

/** Count-up duration. Long enough to read as a transition, short enough to stay snappy. */
const SCORE_TWEEN_MS = 480;
/** How long the gain/loss colour signal stays lit after the count-up starts. */
const SCORE_FLASH_MS = 620;
/** How long a row keeps its "just re-scored" tint. */
const ROW_SETTLE_MS = 620;

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const toFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

// --- The shared ticker ---------------------------------------------------------------
// Every tween on the page rides the same rAF loop. The loop only exists while at least one
// tween is running, so an idle table costs nothing.

type FrameTicker = (now: number) => void;

const activeTickers = new Set<FrameTicker>();
let scheduledFrameId: number | null = null;

const runFrame = (now: number) => {
  scheduledFrameId = null;
  // Snapshot: a ticker unsubscribes itself on its last frame, and mutating the live set
  // while iterating it would skip a neighbour.
  const snapshot = Array.from(activeTickers);
  for (const ticker of snapshot) ticker(now);
  if (activeTickers.size > 0) scheduledFrameId = requestAnimationFrame(runFrame);
};

const subscribeToFrames = (ticker: FrameTicker): (() => void) => {
  if (typeof requestAnimationFrame !== 'function') {
    return () => undefined;
  }
  activeTickers.add(ticker);
  if (scheduledFrameId === null) scheduledFrameId = requestAnimationFrame(runFrame);
  return () => {
    activeTickers.delete(ticker);
  };
};

const easeOutCubic = (progress: number): number => 1 - Math.pow(1 - progress, 3);

// --- The visibility gate -------------------------------------------------------------
// A shortlist can hold hundreds of rows and a re-score rewrites all of them, but only a
// screenful is worth animating. One shared IntersectionObserver answers "is this on screen?"
// for every animated node: the browser reports it asynchronously, so asking costs nothing ,
// unlike getBoundingClientRect, which would force a reflow per row in the middle of a burst.

const OFFSCREEN_ROOT_MARGIN = '200px';

const visibilityByElement = new WeakMap<Element, boolean>();
let sharedVisibilityObserver: IntersectionObserver | null = null;

const getSharedVisibilityObserver = (): IntersectionObserver | null => {
  if (typeof IntersectionObserver !== 'function') return null;
  if (!sharedVisibilityObserver) {
    sharedVisibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) visibilityByElement.set(entry.target, entry.isIntersecting);
      },
      { rootMargin: OFFSCREEN_ROOT_MARGIN }
    );
  }
  return sharedVisibilityObserver;
};

const observeVisibility = (element: Element): (() => void) => {
  const observer = getSharedVisibilityObserver();
  if (!observer) return () => undefined;
  observer.observe(element);
  return () => {
    observer.unobserve(element);
    visibilityByElement.delete(element);
  };
};

/** Unknown counts as visible: the observer has not reported yet, and a missed frame of animation
 *  is a better failure than silently skipping the animation the user is looking at. */
const isProbablyOnScreen = (element: Element | null): boolean =>
  !element || visibilityByElement.get(element) !== false;

// --- Tweened number ------------------------------------------------------------------

/**
 * Returns a ref callback for the element that displays `value`. Whenever `value` genuinely
 * CHANGES the element's text counts from the currently displayed number to the new one and
 * carries a `data-score-trend` marker (`up` / `down`) for the colour signal.
 *
 * The change test is against the last TARGET, held in a ref, not against the previous
 * render, so the hundreds of unrelated re-renders a shortlist row goes through (brand
 * hydration, hover, selection, sibling updates) never restart an animation.
 */
function useTweenedNumberRef(
  value: number | null | undefined
): (element: HTMLElement | null) => void {
  const elementRef = useRef<HTMLElement | null>(null);
  const targetRef = useRef<number | null>(toFiniteNumber(value));
  const displayedRef = useRef<number | null>(toFiniteNumber(value));
  const stopTweenRef = useRef<(() => void) | null>(null);
  const trendTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trendRef = useRef<'up' | 'down' | null>(null);

  const writeToElement = useCallback((next: number | null) => {
    const element = elementRef.current;
    if (!element) return;
    element.textContent = next === null ? '' : String(next);
  }, []);

  const clearTrend = useCallback(() => {
    if (trendTimeoutRef.current) {
      clearTimeout(trendTimeoutRef.current);
      trendTimeoutRef.current = null;
    }
    trendRef.current = null;
    const element = elementRef.current;
    if (element) element.removeAttribute('data-score-trend');
  }, []);

  const unobserveRef = useRef<(() => void) | null>(null);

  const attach = useCallback((element: HTMLElement | null) => {
    unobserveRef.current?.();
    unobserveRef.current = null;
    elementRef.current = element;
    if (!element) return;
    unobserveRef.current = observeVisibility(element);
    // A row can be re-attached mid-tween (React re-parents on column changes): repaint the
    // value we are currently showing so the node never falls back to its mount-time text.
    if (displayedRef.current !== targetRef.current) {
      element.textContent = displayedRef.current === null ? '' : String(displayedRef.current);
    }
  }, []);

  useEffect(() => {
    const nextTarget = toFiniteNumber(value);
    if (nextTarget === targetRef.current) return;

    const previousTarget = targetRef.current;
    targetRef.current = nextTarget;

    stopTweenRef.current?.();
    stopTweenRef.current = null;

    // First real value, a cleared value, a row scrolled out of sight, or reduced motion: land
    // on it, no theatre. Off-screen rows are the reason a 300-row re-score stays cheap.
    if (
      previousTarget === null ||
      nextTarget === null ||
      !isProbablyOnScreen(elementRef.current) ||
      prefersReducedMotion()
    ) {
      displayedRef.current = nextTarget;
      writeToElement(nextTarget);
      clearTrend();
      return;
    }

    const from = displayedRef.current ?? previousTarget;
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const direction: 'up' | 'down' = nextTarget > previousTarget ? 'up' : 'down';

    const element = elementRef.current;
    if (element) {
      // Re-setting the same attribute value is deliberately a no-op rather than a restart:
      // restarting a CSS animation needs a forced reflow, and a re-score changes hundreds of
      // rows in one commit. Extending the existing flash costs nothing.
      if (trendRef.current !== direction) {
        trendRef.current = direction;
        element.setAttribute('data-score-trend', direction);
      }
      if (trendTimeoutRef.current) clearTimeout(trendTimeoutRef.current);
      trendTimeoutRef.current = setTimeout(clearTrend, SCORE_FLASH_MS);
    }

    stopTweenRef.current = subscribeToFrames((now) => {
      const progress = Math.min(1, Math.max(0, (now - startedAt) / SCORE_TWEEN_MS));
      const frameValue = Math.round(from + (nextTarget - from) * easeOutCubic(progress));
      if (frameValue !== displayedRef.current) {
        displayedRef.current = frameValue;
        writeToElement(frameValue);
      }
      if (progress >= 1) {
        displayedRef.current = nextTarget;
        writeToElement(nextTarget);
        stopTweenRef.current?.();
        stopTweenRef.current = null;
      }
    });
  }, [value, writeToElement, clearTrend]);

  useEffect(
    () => () => {
      stopTweenRef.current?.();
      stopTweenRef.current = null;
      if (trendTimeoutRef.current) clearTimeout(trendTimeoutRef.current);
      unobserveRef.current?.();
      unobserveRef.current = null;
    },
    []
  );

  return attach;
}

export interface AnimatedScoreProps {
  value: number | null | undefined;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The number itself. React renders the MOUNT value once and never touches the text again
 * (the rendered child is frozen in a ref), which is what lets the tween own `textContent`
 * without React fighting it back on the next commit.
 */
export const AnimatedScore = React.memo(({ value, className, style }: AnimatedScoreProps) => {
  const attach = useTweenedNumberRef(value);
  // Frozen at mount and never set again, so every subsequent render produces the SAME child and
  // React has no reason to patch the text node the tween is writing to.
  const [mountValue] = useState<number | null | undefined>(value);
  return (
    <span ref={attach} className={clsx('score-value', className)} style={style}>
      {mountValue}
    </span>
  );
});

AnimatedScore.displayName = 'AnimatedScore';

// --- Row settle ----------------------------------------------------------------------

/**
 * Returns a ref callback for a row element. Each time `value` changes the element carries
 * `data-score-settled` ("up" / "down") for ROW_SETTLE_MS, which the stylesheet turns into a
 * short inset tint. Rows therefore settle one by one as their new scores land, instead of
 * the whole list flipping in one atomic swap.
 */
export function useScoreSettleRef(
  value: number | null | undefined
): (element: HTMLElement | null) => void {
  const elementRef = useRef<HTMLElement | null>(null);
  const lastValueRef = useRef<number | null>(toFiniteNumber(value));
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const unobserveRef = useRef<(() => void) | null>(null);

  const attach = useCallback((element: HTMLElement | null) => {
    unobserveRef.current?.();
    unobserveRef.current = null;
    elementRef.current = element;
    if (element) unobserveRef.current = observeVisibility(element);
  }, []);

  useEffect(() => {
    const nextValue = toFiniteNumber(value);
    if (nextValue === lastValueRef.current) return;
    const previousValue = lastValueRef.current;
    lastValueRef.current = nextValue;
    if (previousValue === null || nextValue === null) return;

    const element = elementRef.current;
    if (!element || !isProbablyOnScreen(element) || prefersReducedMotion()) return;

    element.setAttribute('data-score-settled', nextValue > previousValue ? 'up' : 'down');
    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = setTimeout(() => {
      elementRef.current?.removeAttribute('data-score-settled');
    }, ROW_SETTLE_MS);
  }, [value]);

  useEffect(
    () => () => {
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
      unobserveRef.current?.();
      unobserveRef.current = null;
    },
    []
  );

  return attach;
}
